<template>
  <div class="esm-bg" @click.self="$emit('close')">
    <div class="esm-modal" @click.stop>
      <div class="esm-head">
        <div
          class="esm-head-icon"
          v-html="iconMarkup"
        ></div>
        <div>
          <h2>{{ $t('Edit workgroup') }}</h2>
          <div class="sub">{{ $t('Customise how this workgroup appears in the sidebar.') }}</div>
        </div>
        <button type="button" class="esm-x" @click="$emit('close')" :title="$t('Close')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="esm-body">
          <h5 class="esm-section-ttl">{{ $t('Visibility') }}</h5>
          <div class="esm-status">
            <button
              type="button"
              :class="['esm-status-opt', { on: form.status === 'draft' }]"
              @click="form.status = 'draft'"
            >
              <div class="esm-status-row">
                <span class="esm-status-dot draft"></span>
                <span class="esm-status-label">{{ $t('Draft') }}</span>
              </div>
              <span class="esm-status-hint">{{ $t('Hidden from everyone except you and other workgroup members.') }}</span>
            </button>
            <button
              type="button"
              :class="['esm-status-opt', { on: form.status === 'published' }]"
              @click="form.status = 'published'"
            >
              <div class="esm-status-row">
                <span class="esm-status-dot published"></span>
                <span class="esm-status-label">{{ $t('Published') }}</span>
              </div>
              <span class="esm-status-hint">{{ $t('Visible in the sidebar for every employee.') }}</span>
            </button>
          </div>

          <div class="esm-row" style="margin-top: 18px;">
            <label class="esm-lbl">{{ $t('Workgroup name') }}</label>
            <input class="esm-fld" type="text" v-model="form.name" />
            <div class="esm-hint">{{ $t('Appears in the sidebar and breadcrumbs.') }}</div>
          </div>
          <div class="esm-row">
            <label class="esm-lbl">{{ $t('Slug') }}</label>
            <input class="esm-fld" type="text" v-model="form.slug" />
            <div class="esm-hint">/spaces/<span style="color: var(--cs-brand-blue, #0077BD);">{{ form.slug || '…' }}</span>/…</div>
          </div>
          <div class="esm-row">
            <label class="esm-lbl">{{ $t('Description') }}</label>
            <textarea
              class="esm-fld"
              rows="3"
              v-model="form.description"
              :placeholder="$t('Short description shown on the workgroup landing.')"
            ></textarea>
          </div>

          <h5 class="esm-section-ttl">{{ $t('Icon') }}</h5>
          <div class="esm-icon-grid">
            <button
              v-for="opt in iconOptions"
              :key="opt.id"
              type="button"
              :class="['esm-ico-pick', { on: form.iconKey === opt.id }]"
              @click="setIcon(opt.id)"
              v-html="opt.svg"
            ></button>
          </div>
      </div>

      <div class="esm-foot">
        <button type="button" class="esm-btn ghost" @click="$emit('close')">{{ $t('Cancel') }}</button>
        <button
          type="button"
          class="esm-btn primary"
          :disabled="saving"
          @click="onSave"
        >
          <svg v-if="!saving" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {{ saving ? $t('Saving…') : $t('Save changes') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useSpaces } from '../../../composables/useSpaces.js';
import { useToast } from 'vue-toastification';

const props = defineProps({
  space: { type: Object, required: true }
});
const emit = defineEmits(['close', 'saved']);

const toast = useToast();
const { updateSpace, load: reloadSpaces } = useSpaces();

const saving = ref(false);

const form = ref({
  name: '',
  slug: '',
  description: '',
  status: 'draft',
  iconKey: 'shield',
  iconSvg: ''
});

const iconOptions = [
  { id: 'shield', svg: svg('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>') },
  { id: 'bell', svg: svg('<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>') },
  { id: 'beaker', svg: svg('<path d="M9 2v6L4 18a2 2 0 0 0 1.7 3h12.6A2 2 0 0 0 20 18L15 8V2"/><path d="M8 14h8"/>') },
  { id: 'drop', svg: svg('<path d="M12 2.5C8 7 5 11 5 14a7 7 0 0 0 14 0c0-3-3-7-7-11.5z"/>') },
  { id: 'heart', svg: svg('<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>') },
  { id: 'cog', svg: svg('<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>') },
  { id: 'bolt', svg: svg('<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>') },
  { id: 'building', svg: svg('<rect x="4" y="2" width="16" height="20" rx="2"/><line x1="9" y1="22" x2="9" y2="2"/><line x1="15" y1="22" x2="15" y2="2"/>') },
  { id: 'users', svg: svg('<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>') },
  { id: 'folder', svg: svg('<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>') },
  { id: 'globe', svg: svg('<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>') },
  { id: 'cal', svg: svg('<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>') },
  { id: 'flag', svg: svg('<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>') },
  { id: 'mega', svg: svg('<path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>') }
];

function svg(paths) {
  return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
}

const iconMarkup = computed(() => {
  const found = iconOptions.find((i) => i.id === form.value.iconKey);
  return found ? found.svg : (form.value.iconSvg || iconOptions[0].svg);
});

function setIcon(key) {
  form.value.iconKey = key;
  const found = iconOptions.find((i) => i.id === key);
  if (found) form.value.iconSvg = found.svg;
}

watch(
  () => props.space,
  (s) => hydrateForm(s),
  { immediate: true, deep: false }
);

function hydrateForm(s) {
  // Match the existing icon SVG to one of our presets if possible
  const matchedKey = iconOptions.find((opt) => opt.svg === s.icon)?.id || 'shield';
  form.value = {
    name: s.name || '',
    slug: s.slug || '',
    description: s.description || '',
    status: s.status || 'draft',
    iconKey: matchedKey,
    iconSvg: s.icon || iconOptions[0].svg
  };
}

async function onSave() {
  saving.value = true;
  try {
    await updateSpace(props.space.id, {
      name: form.value.name,
      slug: form.value.slug,
      description: form.value.description,
      status: form.value.status,
      icon: iconMarkup.value
    });
    await reloadSpaces({ forceRefresh: true, includeDrafts: true });
    toast.success('Workgroup saved');
    emit('saved');
    emit('close');
  } catch (e) {
    console.error(e);
    toast.error('Failed to save workgroup');
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.esm-bg {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  animation: esm-fadein 0.16s ease-out;
}

@keyframes esm-fadein {
  from { opacity: 0; }
  to { opacity: 1; }
}

.esm-modal {
  background: var(--cs-mono-white, #fff);
  border-radius: 10px;
  width: 600px;
  max-width: 92vw;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 32px rgba(23, 35, 61, 0.18);
  animation: esm-rise 0.2s cubic-bezier(.2, 0, 0, 1);
}

@keyframes esm-rise {
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.esm-head {
  padding: 18px 24px 12px;
  border-bottom: 1px solid var(--cs-mono-subtle-div, #E8EAEC);
  display: flex;
  align-items: center;
  gap: 12px;
}

.esm-head-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--cs-blue-50, #D1E6F6);
  color: var(--cs-brand-blue, #0077BD);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.esm-head-icon :deep(svg) {
  width: 20px;
  height: 20px;
}

.esm-head h2 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--cs-mono-ink-dark, #09121F);
}

.esm-head .sub {
  font-size: 12px;
  color: var(--cs-mono-caption, #808695);
  margin-top: 2px;
}

.esm-x {
  margin-left: auto;
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--cs-mono-caption, #808695);
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.esm-x:hover {
  background: var(--cs-mono-subtle-div, #E8EAEC);
  color: var(--cs-mono-ink, #17233D);
}

.esm-body {
  padding: 20px 24px 24px;
  overflow-y: auto;
  flex: 1;
}

.esm-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

.esm-lbl {
  font-size: 12px;
  font-weight: 500;
  color: var(--cs-mono-primary, #313331);
}

.esm-hint {
  font-size: 11px;
  color: var(--cs-mono-caption, #808695);
}

.esm-fld {
  appearance: none;
  width: 100%;
  border: 1px solid var(--cs-mono-divider, #D0DEE2);
  border-radius: 6px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  color: var(--cs-mono-ink, #17233D);
  background: var(--cs-mono-white, #fff);
  outline: none;
}

.esm-fld:focus {
  border-color: var(--cs-brand-blue, #0077BD);
  box-shadow: 0 0 0 3px rgba(0, 119, 189, 0.18);
}

textarea.esm-fld {
  resize: vertical;
  min-height: 64px;
  line-height: 1.5;
  font-family: inherit;
}

.esm-section-ttl {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--cs-mono-caption, #808695);
  margin: 14px 0 10px;
}

.esm-icon-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
}

.esm-ico-pick {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--cs-mono-subtle-div, #E8EAEC);
  border-radius: 6px;
  cursor: pointer;
  color: var(--cs-mono-secondary, #6C6F78);
  background: var(--cs-mono-white, #fff);
  transition: all 0.12s ease;
}

.esm-ico-pick:hover {
  border-color: var(--cs-brand-blue, #0077BD);
  color: var(--cs-brand-blue, #0077BD);
}

.esm-ico-pick.on {
  background: var(--cs-blue-50, #D1E6F6);
  border-color: var(--cs-brand-blue, #0077BD);
  color: var(--cs-brand-blue, #0077BD);
}

.esm-ico-pick :deep(svg) {
  width: 16px;
  height: 16px;
}

/* Visibility picker */
.esm-status {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.esm-status-opt {
  appearance: none;
  background: var(--cs-mono-white, #fff);
  border: 1px solid var(--cs-mono-subtle-div, #E8EAEC);
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  display: flex;
  flex-direction: column;
  gap: 2px;
  transition: border-color 0.12s ease, background 0.12s ease;
}

.esm-status-opt:hover {
  border-color: var(--cs-mono-divider, #D0DEE2);
}

.esm-status-opt.on {
  border-color: var(--cs-brand-blue, #0077BD);
  background: var(--cs-blue-50, #D1E6F6);
}

.esm-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.esm-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.esm-status-dot.draft {
  background: var(--cs-mono-secondary, #6C6F78);
}

.esm-status-dot.published {
  background: var(--cs-accent-green, #73BF43);
}

.esm-status-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--cs-mono-ink, #17233D);
}

.esm-status-hint {
  font-size: 11px;
  color: var(--cs-mono-secondary, #6C6F78);
  margin-left: 16px;
  line-height: 1.4;
}

.esm-foot {
  padding: 12px 16px;
  border-top: 1px solid var(--cs-mono-subtle-div, #E8EAEC);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: var(--cs-mono-bg, #F8F8F9);
  border-radius: 0 0 10px 10px;
}

.esm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
}

.esm-btn.ghost {
  color: var(--cs-mono-secondary, #6C6F78);
}

.esm-btn.ghost:hover {
  background: var(--cs-mono-subtle-div, #E8EAEC);
  color: var(--cs-mono-ink, #17233D);
}

.esm-btn.primary {
  background: var(--cs-brand-blue, #0077BD);
  color: white;
}

.esm-btn.primary:hover {
  background: #0063A9;
}

.esm-btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
